<script setup lang="ts">
import Modal from '@/components/common/layout/Modal.vue'
import { computed, defineProps, inject, ref, watch } from 'vue'
import type { UpdateEventDto } from '@/api-swagger'
import { usePartnerStore } from '@/stores/usePartnerStore'
import { useEventStore } from '@/stores/useEventStore'
import { useContactStore } from '@/stores/useContactStore'
import { storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'
import { differenceInHours } from 'date-fns'
import { calculateDurationWithFormat, removeTimeZone } from '@/utils/dateUtils'
import { useToast } from 'vue-toast-notification'
import { useUserStore } from '@/stores/useUserStore'
import '@vuepic/vue-datepicker/dist/main.css'
import TextInput from '@/components/common/inputs/TextInput.vue'
import SelectSimple from '@/components/common/selects/SelectSimple.vue'
import SaveButton from '@/components/common/buttons/SaveButton.vue'
import { VQuillEditor } from '@morpheme/quill-editor'
import { useForm } from 'vee-validate'
import { object, string } from 'yup'
import SelectWithAvatar from '@/components/common/selects/SelectWithAvatar.vue'
import UserCard from '@/components/common/ui/UserCard.vue'
import DatetimePicker from '@/components/common/inputs/DatetimePicker.vue'
import type { Context } from '@/utils/types'
import ContactCreateModal from '@/components/features/contact/ContactCreateModal.vue'
import PartnerButtonRedirect from '@/components/common/buttons/PartnerButtonRedirect.vue'
import { useAttachmentStore } from '@/stores/useAttachmentStore'
import PreviewFiles from '@/components/common/ui/PreviewFiles.vue'
import { TrashIcon } from '@heroicons/vue/24/outline'
import IconWithTooltip from '@/components/common/ui/IconWithTooltip.vue'
import LoaderCenter from '@/components/common/feedback/LoaderCenter.vue'
import FileInput from '@/components/common/inputs/FileInput.vue'
import type { FileInputExpose } from '@/types/fileInput'

const attachmentStore = useAttachmentStore()
const attachmentRef = ref<FileInputExpose | null>(null)
const route = useRoute()
let partnerId: number | null = parseInt(route.params.partnerId as string)
const partnerStore = usePartnerStore()
const userStore = useUserStore()
const eventStore = useEventStore()
const contactStore = useContactStore()
const { selectedEvent, loading, categories } = storeToRefs(eventStore)
const { userConnected, users } = storeToRefs(userStore)
const { contacts } = storeToRefs(contactStore)
const { partnerForEventSelected } = storeToRefs(partnerStore)
const $toast = useToast()
const emit = defineEmits(['close', 'save'])
const props = defineProps({
  eventId: {
    type: Number,
    required: true,
  },
})
const isOnDelete = ref<boolean>(false)

const startDate = ref()
const endDate = ref()
const sendMail = ref(false)

const contactPartner = ref()
const contactUser = ref()
const loadingContactsPartner = ref(true)
const preselectedCompany = ref<{ uuid: string; name: string } | undefined>(undefined)

const handleCreateContact = async () => {
  if (partnerId) {
    await fetchSuiteContacts(partnerId)
  }
}

const errors = ref<Record<string, string>>({})
const context = inject<Context>('context', '')

const fetchSuiteContacts = async (odooPartnerId: number) => {
  const company = await partnerStore.fetchCompanyByOdooRef(odooPartnerId)
  if (company?.uuid) {
    await contactStore.fetchContacts({ companyUuid: [company.uuid] }, 1, 100)
  }
}

const schema = object({
  content: string().required().label('Content'),
})

const { values } = useForm({
  validationSchema: schema,
})

if (!route.path.includes('diary')) {
  fetchSuiteContacts(partnerId!)
}

watch(
  () => props.eventId,
  async (newId) => {
    if (newId) {
      await eventStore.getEventById(newId)

      if (selectedEvent.value) {
        startDate.value = new Date(selectedEvent.value.start)
        endDate.value = new Date(selectedEvent.value.stop)
        await attachmentStore.getAllAttachmentByRessourceId(selectedEvent.value.id)
        if (selectedEvent.value.partnerIds) {
          partnerId =
            selectedEvent.value.partnerSelected?.id ||
            (
              selectedEvent.value.partnerIds.map((partner) => {
                if (partner.parentId && partner.parentId != userConnected?.value?.companyId?.id) {
                  return partner.parentId
                } else if (
                  partner.id != userConnected?.value?.companyId?.id &&
                  partner.id == userConnected.value?.id
                ) {
                  return partner.id
                }
                return null
              }) || []
            ).filter(Boolean)[0] ||
            0
          if (partnerId) {
            partnerStore.getPartnerNameById(partnerId)
            fetchSuiteContacts(partnerId).then(() => {
              loadingContactsPartner.value = false
            })

            partnerStore.fetchCompanyByOdooRef(partnerId).then((company) => {
              if (company) {
                preselectedCompany.value = { uuid: company.uuid, name: company.name }
              }
            })
          } else {
            partnerStore.getPartnerNameById(partnerId)
            loadingContactsPartner.value = false
          }
          contactPartner.value = selectedEvent.value.partnerIds
            .map((partner) => {
              if (
                partnerId == partner.id ||
                (partnerId == partner.parentId &&
                  partner.email !=
                    userStore.getLocalInfoUserById(selectedEvent.value?.userId || 0)?.email)
              ) {
                return partner.id
              }
            })
            .filter(Boolean)

          contactUser.value = selectedEvent.value.partnerIds
            .map((partner) => {
              if (
                partnerId != partner.id &&
                partnerId !== partner.parentId &&
                partner.email !=
                  userStore.getLocalInfoUserById(selectedEvent.value?.userId || 0)?.email
              ) {
                return partner.id
              }
            })
            .filter(Boolean)
        }
      }
    }
  },
  { immediate: true },
)

watch(startDate, (newValue, oldValue) => {
  // Quand la date de début change (sans l'heure), on modifie la date de fin
  if (newValue && oldValue) {
    if (
      newValue.getFullYear() !== oldValue.getFullYear() ||
      newValue.getMonth() !== oldValue.getMonth() ||
      newValue.getDate() !== oldValue.getDate()
    ) {
      const newDate = new Date(endDate.value)
      newDate.setFullYear(newValue.getFullYear())
      newDate.setMonth(newValue.getMonth())
      newDate.setDate(newValue.getDate())

      endDate.value = newDate
    }

    if (
      newValue.getHours() !== oldValue.getHours() ||
      newValue.getMinutes() !== oldValue.getMinutes()
    ) {
      const newDate = new Date(endDate.value)
      newDate.setHours(
        newValue.getHours() + differenceInHours(endDate.value, oldValue),
        newValue.getMinutes(),
      )
      endDate.value = newDate
    }
  }
})

const onSubmit = async () => {
  errors.value = {}
  const fileInput = attachmentRef.value?.fileInputRef
  const files = fileInput?.files ? Array.from(fileInput.files) : []

  if (selectedEvent.value) {
    if (startDate.value && endDate.value && endDate.value <= startDate.value) {
      errors.value['endDate'] = 'La date de fin ne peut pas être avant la date de début.'
      return
    }

    const duration = calculateDurationWithFormat(startDate.value, endDate.value)
    const updateEventDto: UpdateEventDto = {
      name: selectedEvent.value.name,
      start: removeTimeZone(startDate.value),
      duration: duration,
      partnerIds: {},
      categIds: selectedEvent.value.categIds,
      description: values.description,
      userId: selectedEvent.value.userId,
      location: selectedEvent.value.location,
      partnerSelected: selectedEvent.value.partnerSelected?.id || null,
      contextTracking: sendMail.value,
    }

    // Make sure to include the event creator in the partnerIds sent to the backend
    const partnerIdsArray = [...contactPartner.value, ...contactUser.value]

    // Add the event creator to partnerIds if they're not already included
    if (selectedEvent.value.userId) {
      const creatorPartnerId = userStore.getLocalInfoUserById(selectedEvent.value.userId)?.partnerId
        ?.id
      if (creatorPartnerId && !partnerIdsArray.includes(creatorPartnerId)) {
        partnerIdsArray.push(creatorPartnerId)
      }
    }

    await eventStore
      .updateEvent(
        {
          id: selectedEvent.value.id,
          partnerIds: selectedEvent.value.partnerIds!.map((p) => p.id),
        },
        updateEventDto,
        partnerIdsArray,
        context,
      )
      .then(() => {
        $toast.success('Rendez-vous mis à jour avec succès.')
        if (attachmentRef.value) {
          if (files && files.length > 0) {
            attachmentStore
              .uploadAttachments(files, 'calendar.event', props.eventId)
              .then(() => {
                $toast.success('Pièces jointes enregistrées avec succès.')
              })
              .catch((error: { message: string }) => {
                $toast.error(
                  `Erreur lors de l'enregistrement des pièces jointes : ${error.message}`,
                )
              })
          }
        }
      })
      .catch((error) => {
        $toast.error(error.message)
      })
      .finally(() => {
        emit('close')
      })
  }
}

const deleteEvent = async () => {
  await eventStore
    .deleteById(selectedEvent.value!.id)
    .then(async () => {
      selectedEvent.value = undefined
      await eventStore.getMyEvents()
      await eventStore.getEventsCharts()
      await eventStore.getEventsByUserEmailAndCurrentMonth()
      await eventStore.getEventsSubordinatesId()
      $toast.success('Rendez-vous supprimé avec succès')
    })
    .catch((error) => {
      $toast.error(error.message)
    })
    .finally(() => {
      emit('close')
    })
}
const optionsChilds = computed(() => {
  return (
    contacts.value?.data?.filter((c) => !!c.referenceExterne).map((contact) => ({
      id: Number(contact.referenceExterne) || 0,
      text: [contact.firstname, contact.lastname].filter(Boolean).join(' ') || '',
      subText: contact.email || '',
    })) || []
  )
})

const optionsUsers = computed(() => {
  return (
    users?.value?.map((u) => {
      return {
        id: u.partnerId?.id || 0,
        text: u.name,
        subText: u.email || '',
      }
    }) || []
  )
})
</script>

<template>
  <Modal :showModal="true" @close="emit('close')" title="Détails du Rendez-vous" :loading="loading">
    <template #content>
      <loader-center v-if="loading || !selectedEvent" class="h-16" />
      <form @submit.prevent="" v-if="selectedEvent && !loading" class="space-y-4">
        <div class="grid lg:grid-cols-3 gap-4">
          <div class="col-span-2">
            <TextInput
              v-model="selectedEvent!.name"
              label="Sujet"
              placeholder="Sujet"
              :disabled="!eventStore.isMyEvent(selectedEvent)"
            />
          </div>
          <SelectSimple
            id="event-category"
            :options="
              categories?.map((category) => {
                return {
                  id: category.id.toString(),
                  value: category.id.toString(),
                  label: category.name,
                }
              })
            "
            v-model="selectedEvent!.categIds"
            label="Étiquette"
            required
            :disabled="!eventStore.isMyEvent(selectedEvent)"
          />
        </div>

        <div class="grid xl:grid-cols-2 gap-4">
          <div>
            <label class="label" for="description">
              <span class="label-text font-semibold"> Dates </span>
            </label>
            <DatetimePicker
              label="Début"
              v-model="startDate"
              :errorMessage="errors.startDate"
              :disabled="!eventStore.isMyEvent(selectedEvent)"
            />
            <DatetimePicker
              label="Fin"
              v-model="endDate"
              :errorMessage="errors.endDate"
              :disabled="!eventStore.isMyEvent(selectedEvent)"
            />
          </div>
        </div>
        <div class="grid lg:grid-cols-12 gap-4">
          <span class="col-span-6 content-center" v-if="partnerForEventSelected">
            <PartnerButtonRedirect
              :partner-name="partnerForEventSelected?.name"
              :partner-id="partnerForEventSelected?.id"
            ></PartnerButtonRedirect>
          </span>
          <TextInput
            class="col-span-6"
            v-model="selectedEvent!.location"
            label="Lieu"
            placeholder="Lieu"
            :disabled="!eventStore.isMyEvent(selectedEvent)"
          />
          <loader-center v-if="loadingContactsPartner" class="col-span-12 h-16" />
          <SelectWithAvatar
            class="col-span-6"
            v-if="!loadingContactsPartner && contactPartner"
            v-model="contactPartner"
            :options="optionsChilds"
            :show-avatar="false"
            label="Participants Client"
            multiple
            :disabled="!eventStore.isMyEvent(selectedEvent)"
          />
          <div
            v-if="partnerId && eventStore.isMyEvent(selectedEvent)"
            class="col-auto flex flex-col justify-end"
          >
            <div class="flex">
              <ContactCreateModal
                :preselectedCompany="preselectedCompany"
                lightweight
                @created="handleCreateContact"
              />
            </div>
          </div>
          <SelectWithAvatar
            class="col-span-6"
            v-if="!loadingContactsPartner && contactUser"
            v-model="contactUser"
            :options="optionsUsers"
            label="Collaborateurs"
            multiple
            required
            :disabled="!eventStore.isMyEvent(selectedEvent)"
          />
          <div class="col-span-6 gap-4">
            <div class="label">
              <span class="label-text font-semibold">
                Envoyer une invitation mail aux participants
              </span>
              <input type="checkbox" v-model="sendMail" class="toggle toggle-xs" />
            </div>
          </div>
          <div class="col-span-6" v-if="selectedEvent && selectedEvent.userId">
            <label class="label">
              <span class="label-text font-semibold">Organisateur</span>
            </label>
            <UserCard
              :name="userStore.getLocalInfoUserById(selectedEvent.userId)?.name || 'Inconnu'"
              :email="userStore.getLocalInfoUserById(selectedEvent.userId)?.email || ''"
            />
          </div>
        </div>
        <div>
          <label class="label" for="description">
            <span class="label-text font-semibold"> Description </span>
          </label>
          <VQuillEditor
            name="description"
            v-model="selectedEvent!.description"
            :readOnly="!eventStore.isMyEvent(selectedEvent)"
            :placeholder="eventStore.isMyEvent(selectedEvent) ? 'Description' : ''"
          />
        </div>
        <div role="alert" class="alert alert-warning w-auto mx-auto" v-if="isOnDelete">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6 shrink-0 stroke-current"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <span>Cela va supprimer le rendez-vous</span>
          <div class="flex gap-2">
            <button class="btn btn-sm" @click="isOnDelete = false">Annuler</button>
            <button class="btn btn-sm btn-primary" @click="deleteEvent">Supprimer</button>
          </div>
        </div>
        <div class="flex justify-between gap-4">
          <FileInput ref="attachmentRef" label="Pièces jointes" :multiple="true" />

          <SaveButton
            :loading="loading"
            @save="onSubmit"
            :disabled="isOnDelete || !eventStore.isMyEvent(selectedEvent)"
          >
            <icon-with-tooltip
              :loading="loading"
              :disabled="isOnDelete || !eventStore.isMyEvent(selectedEvent)"
              :variant="'error'"
              :text="'Supprimer'"
              @click="isOnDelete = true"
              :icon="TrashIcon"
            />
          </SaveButton>
        </div>
      </form>
      <PreviewFiles
        v-if="selectedEvent"
        :ressourceId="selectedEvent.id"
        :disabled="!eventStore.isMyEvent(selectedEvent)"
      />
    </template>
  </Modal>
</template>
